import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ElementRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { isUndefined } from '../../models/utils';
import { DrNamedQuery, DrNamedQueryViewModel } from '../../models/dr-named-query';
import { DrObject, DrObjectType, SupportedRegExType } from '../../models/dr-object';
import { KeyValueDataSource } from './key-value-data-source';
import { cloneDeep } from 'lodash';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { Endpoint, SettingsType } from '../../models/enums';
import { MetadataService } from '../../services/metadata/metadata.service';

@Component({
    standalone: false,
    selector: 'res-key-value-list',
    templateUrl: './key-value-list.component.html',
    styleUrls: ['./key-value-list.component.scss']
})
export class KeyValueListComponent implements OnInit {
    @ViewChild('keyValueForm', { static: true }) keyValueForm: NgForm;
    @ViewChild(CdkVirtualScrollViewport, { static: true }) cdkVirtualScrollViewport: CdkVirtualScrollViewport;

    get rows(): Array<any> {
        return this.dataSource.data;
    }
    @Input()
    set rows(val: Array<any>) {
        if (val && val.length > 0) {
            if (this.isDisabled) {
                const hasEmptyRow = val[val.length - 1].name === '';
                if (hasEmptyRow) {
                    val.pop();
                }
            }
            this.dataSource.set(val);
            this.resetState();
            this.initializeDirtyMap();
        }
    }
    dataSource = new KeyValueDataSource<any>();
    currentFocusedIndex: number;
    errorIndices: Array<number>;

    @Input() placeholderField1: string;
    @Input() placeholderField2: string;
    @Input() automationId: string;
    @Input() nameColumnHidden = false;
    @Input() queryColumnHidden = false;
    @Input() removeBoarders = false;
    @Input() validateName: boolean;
    @Input() isEmail: boolean;
    @Input() checkDuplicate: boolean;
    @Input() maxNameLength: number;
    @Input() maxQueryLength: number;
    @Input() queriesChanged: (index?: number, eventType?: string) => void;
    @Input() excludeTrashBtnFromTabIndex = false;
    @Input() showClearNameBtn = false;
    @Input() showClearBtnAlways = false;
    @Input() trackDirty: boolean;
    @Input() pasteSplitOnNewLineOnly = false;
    @Input() isDisabled = false;
    @Input() maxRows: number = undefined;
    @Input() maxRowsLimitReached: (isFromPastedText?: boolean) => void;
    @Input() prefixTemplate: TemplateRef<any>;
    @Input() suffixTemplate: TemplateRef<any>;
    @Input() cdkScrollMinBufferPx = 300;
    @Input() cdkScrollMaxBufferPx = 600;
    @Input() showNumberColumn: false;
    @Input() preserveSpaces: boolean;
    @Input() customizeClass: boolean = false;
    @Input() isQueryColumnAutoPopulated: boolean = false;
    @Input() displayGeneralMessage: boolean = false;
    @Input() isQueryValidationContext: boolean = false;
    @Input() isNameValidationContext: boolean = false;
    @Input() isSurvivorshipComponent: boolean = false;
    @Input() settingsType: SettingsType;

    isClearedByBackspace = false;
    popoverErrors: Array<{
        error: boolean,
        popover: boolean,
        message: string
    }> = new Array<{
        error: boolean,
        popover: boolean,
        message: string
    }>();

    valid: boolean;
    isDirtyMapInitialized: boolean;
    isOriginalKeyValueDeleted: boolean;
    currentKeyValueMap = new Map<number, DrNamedQuery>();
    originalKeyValueMap = new Map<number, DrNamedQuery>();
    isTextPaste: boolean;

    public namedQueryObject: DrNamedQueryViewModel;

    constructor(private element: ElementRef, private commonService: CommonService, private restService: RestService, private xmlService: XMLService, private metadataService: MetadataService) {
        if (this.namedQueryObject === undefined) {
            // Create a default object that is of unknown type and that accepts
            // name validation defaults (SupportedRegExType.GENERIC).
            // The objectType will not matter for this class since the uniqueness checks
            // for these objects is done on save and not as part of validateName().
            this.namedQueryObject = new DrNamedQueryViewModel(DrObjectType.UNKNOWN, new DrNamedQuery());
        }
    }

    ngOnInit(): void {
        this.isDirtyMapInitialized = false;
        this.isOriginalKeyValueDeleted = false;
        this.valid = true;
    }

    focusNextSiblingField(index: number, isName: boolean, isTextPaste = false): void {
        let focusedFieldId = 'name_' + (isTextPaste ? (index - 1) : (index + 1));
        if (isName && !this.queryColumnHidden && !this.isQueryColumnAutoPopulated) {
            focusedFieldId = 'query_' + (isTextPaste ? (index - 1) : (index));
        }
        const fieldToFocus = (this.element.nativeElement as HTMLElement).querySelector('#' + focusedFieldId) as HTMLElement;
        if (fieldToFocus) {
            fieldToFocus.focus();
        }
    }

    checkMaxRowsLimit(isFromPastedText = false): boolean {
        if (this.rows && this.maxRows && this.rows.length >= this.maxRows && this.maxRowsLimitReached) {
            this.maxRowsLimitReached(isFromPastedText);
            return true;
        }
        return false;
    }

    onAddNewRow(index: number, row: any, last: boolean): void {
        this.initializeDirtyMap(last);
        if (!this.checkMaxRowsLimit()) {
            if ((!_.isEmpty(row.name) || !_.isEmpty(row.query)) && last) {
                this.addRow({ name: '', query: '', description: null, annotationText: null });
            } else if ((_.isEmpty(row.name) && _.isEmpty(row.query)) && !last && !this.isClearedByBackspace) {
                this.removeRow(index);
                this.isClearedByBackspace = false;
            }
        }
    }

    onRemoveRow(index: number, row: any, last: boolean): void {
        this.initializeDirtyMap();
        if (this.rows[index]) {
            // need to recalculate error indices as indices may change after rows are deleted
            this.recalculateErrorIndices();
            if (this.rows[index].hasError) {
                this.queryChanged(row, index, last, "removeRow");
            }
            if (this.validateName) {
                // check if the name exists only once more in the name list and reset that error if so
                const numOfOccurences = this.rows.filter((row: any, i: number) => row.name.toLowerCase() === this.rows[index].name.toLowerCase() && index !== i);
                if (numOfOccurences.length === 1) {
                    this.validateCharacters(numOfOccurences[0].name, this.rows.indexOf(numOfOccurences[0]));
                }
            }
            this.removeRow(index);
        }
    }

    addRow(item: any, insertionIndex?: number): void {
        if (item) {
            if (this.popoverErrors.length !== this.rows.length) {
                this.popoverErrors.length = this.rows.length;
            }
            this.dataSource.push(item, insertionIndex);
            this.cdkVirtualScrollViewport.checkViewportSize();
            this.popoverErrors.push({ error: false, popover: false, message: null });
        }
    }

    removeRow(index: number): void {
        if (index >= 0 && index < this.rows.length) {
            if (this.popoverErrors.length !== this.rows.length) {
                this.popoverErrors.length = this.rows.length;
            }
            this.dataSource.splice(index, 1);
            this.cdkVirtualScrollViewport.checkViewportSize();
            this.popoverErrors.splice(index, 1);

            let finalIndex = index;
            if (!this.currentKeyValueMap.has(index)) {
                const currentIndexes = Array.from(this.currentKeyValueMap.keys());
                finalIndex = currentIndexes[index];
            }
            this.currentKeyValueMap.delete(finalIndex);

            this.dirty();
        }
    }

    clearText(row: any, index: number, last: boolean): void {
        if (document.activeElement.nodeName !== 'INPUT') {
            row.name = '';
            row.hasError = false;
            row.isWarning = false;
            row.isWarningInQueryColumn = false;
            if (this.isEmail) {
                this.popoverErrors[index].error = false;
                this.popoverErrors[index].popover = false;
                this.dirty();
            }
            // need to recalculate error indices as indices may change after rows are cleared
            this.recalculateErrorIndices();
            this.queryChanged(row, index, last, "clearText");
        }
    }

    displayError(index: number, message: string = null): void {
        if (index >= 0) {
            if (index >= this.popoverErrors.length) {
                this.popoverErrors.length = index + 1;
            }
            this.popoverErrors[index] = {
                error: !!message,
                popover: !!message,
                message: message
            };
        }
    }

    onNameFocusChange(event: any, index: number, row: any): void {
        if (row) {
            this.currentFocusedIndex = index;
            if (row.name && row.name !== '') {
                // trim the user typed row name
                row.name = this.preserveSpaces ? row.name : row.name.trim();
                // event.target.value = row.name.trim();

                this.addKeyValueObject(index, row.name, false, row.query);

                if (this.validateName) {
                    // check if the name already exists in the name list
                    const numOfOccurences = this.rows.filter((r: any) => r.name.toLowerCase() === row.name.toLowerCase());

                    if (numOfOccurences.length > 1) {
                        this.displayError(index, this.getDuplicateNameErrorMessage(row.name));
                    } else {
                        this.validateCharacters(row.name, index);
                    }
                }
                this.onEmailChange(index, row);
                this.onCheckDuplicate(index, row);
            } else {
                this.displayError(index);
            }
            if (this.queriesChanged) {
                this.queriesChanged(index);
            }
        }
        this.dirty();
    }

    onEmailChange(index: number, row: any, input?: boolean): void {
        if (this.isEmail && row) {
            this.popoverErrors[index].error = false;
            this.popoverErrors[index].popover = false;
            this.dirty();
            this.namedQueryObject.regex = SupportedRegExType.EMAIL;
            this.validateCharacters(row.name, index);
        }
    }

    onCheckDuplicate(index: number, row: any): void {
        if (this.checkDuplicate && row) {
            this.popoverErrors[index].error = false;
            this.popoverErrors[index].popover = false;
            const trimmedName = DrObject.trimLeadingWhiteSpaces(row?.name);
            const numOfOccurences = this.rows.filter((r: any) => r?.name?.toLowerCase() === trimmedName?.toLowerCase());

            if (numOfOccurences.length > 1 && trimmedName !== '') {
                this.displayError(index, this.getDuplicateNameErrorMessage(row?.name, !this.displayGeneralMessage));
            }

            if (this.isTextPaste) {
                this.rows.forEach((row, rowIndex, object) => {
                    this.popoverErrors[rowIndex] = {
                        error: false,
                        popover: false,
                        message: null
                    };
                });

                const findLastDuplicateIndices = (rows) => {
                    const nameIndexMap = {};
                    const duplicateIndexes = [];

                    for (let i = 0; i < rows.length; i++) {
                        const { name } = rows[i];
                        if (name in nameIndexMap) {
                            // Update map with last index occurrence
                            nameIndexMap[name] = i;
                            duplicateIndexes.push({ name, lastIndex: i });
                        } else {
                            // Store name and index in map
                            nameIndexMap[name] = i;
                        }
                    }

                    return duplicateIndexes;
                }
                const duplicateItemsIndexes = findLastDuplicateIndices(this.rows);
                duplicateItemsIndexes.forEach((row) => {
                    this.displayError(row.lastIndex, this.getDuplicateNameErrorMessage(row?.name, !this.displayGeneralMessage));
                });
                this.isTextPaste = false;
            }
            this.dirty();
        }
    }

    debounceEmailChanged = _.debounce((index: number, row: any) => {
        this.onEmailChange(index, row);
    }, 2000, false);


    nameChanged(row: any, index: number, last: boolean): void {
        if (row) {
            row.hoverIcon = false;

            if (this.rows && this.isDirtyMapInitialized === false) {
                this.initializeDirtyMap(last);
            }
            this.addKeyValueObject(index, row.name, false, row.query);
            this.dirty();
            if (this.validateName) {
                this.validateCharacters(row.name, index);
            }
            if (this.checkDuplicate) {
                this.onCheckDuplicate(index, row)
            }
        }
    }

    queryChanged(row: any, index: number, last: boolean, eventType: string): void {
        if (row) {
            row.hoverIcon = false;

            if (this.queriesChanged) {
                this.queriesChanged(index, eventType);
            }
            this.addKeyValueObject(index, row.name, false, row.query);
            this.dirty();
        }
    }

    pasteText(event: ClipboardEvent, index: number, last: boolean, isName: boolean): void {
        this.initializeDirtyMap(last);
        this.isTextPaste = true;
        const data = event.clipboardData ? event.clipboardData.getData('text/plain') : (window as any).clipboardData.getData('Text');

        if (data && this.rows[this.rows.length - 1].name === '') {
            event.preventDefault();
            let lines: string[];
            if (this.pasteSplitOnNewLineOnly) {
                lines = data.split(/[\n]/).filter((text) => text.trim());
            } else {
                lines = data.split(/[\n,;]/).filter((text) => text.trim());
            }
            if (lines.length > 0) {
                if (!this.isQueryValidationContext || this.isSurvivorshipComponent) {
                    this.dataSource.splice(-1, 1);
                }
                let currentIndex = index;
                const target = event.target as any;
                const prefixSubstring = (target.value as string).substring(0, target.selectionStart);
                const postfixSubstring = (target.value as string).substring(target.selectionEnd);
                const originDataSource = cloneDeep(this.dataSource);
                let currentData = this.dataSource.data[index];
                this.dataSource.splice(index, 1);
                let isMaxRowsLimitReached = false;
                for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
                    const trimmedLine = lines[lineIndex].trim();
                    if (trimmedLine) {
                        if (isName) {
                            this.namedQueryObject.model.name = this.maxNameLength ? trimmedLine.substr(0, this.maxNameLength) : trimmedLine;
                            if (lineIndex === 0) {
                                this.namedQueryObject.model.name = prefixSubstring + this.namedQueryObject.model.name;
                            }
                            if (lineIndex === lines.length - 1) {
                                this.namedQueryObject.model.name = this.namedQueryObject.model.name + postfixSubstring;
                            }
                            if (this.validateName) {
                                this.validateCharacters(this.namedQueryObject.model.name, currentIndex);
                            }
                            this.namedQueryObject.model.name = this.maxNameLength ? this.namedQueryObject.model.name.substr(0, this.maxNameLength) : this.namedQueryObject.model.name;
                            if (currentData && currentData.query) {
                                this.namedQueryObject.model.query = currentData.query;
                            }
                        } else {
                            this.namedQueryObject.model.query = this.maxQueryLength ? trimmedLine.substr(0, this.maxQueryLength) : trimmedLine;
                            if (lineIndex === 0) {
                                this.namedQueryObject.model.query = prefixSubstring + this.namedQueryObject.model.query;
                            }
                            if (lineIndex === lines.length - 1) {
                                this.namedQueryObject.model.query = this.namedQueryObject.model.query + postfixSubstring;
                            }
                            if (currentData && currentData.name) {
                                this.namedQueryObject.model.name = currentData.name;
                            }
                        }
                        isMaxRowsLimitReached = this.checkMaxRowsLimit(true);
                        if (isMaxRowsLimitReached) {
                            break;
                        }
                        const object = new DrNamedQuery(this.namedQueryObject.model);
                        if (!isName && currentIndex !== index && currentIndex <= this.dataSource.data.length - 1) {
                            if (prefixSubstring || postfixSubstring || object.name === '') {
                                this.addRow(object, currentIndex);
                            } else {
                                this.dataSource.data[currentIndex] = object;
                            }
                        } else {
                            if (this.validateName) {
                                if (currentIndex === index || prefixSubstring || postfixSubstring || currentIndex >= this.dataSource.data.length - 1) {
                                    this.addRow(object, currentIndex);
                                } else {
                                    this.dataSource.data[currentIndex] = object;
                                }
                            } else {
                                this.addRow(object, currentIndex);
                            }
                        }
                        if (this.queriesChanged) {
                            this.queriesChanged(currentIndex);
                        }
                        this.addKeyValueToMap(currentIndex, new DrNamedQuery(object), false);
                        currentIndex++;
                        currentData = this.dataSource.data[currentIndex];
                        this.namedQueryObject.model.name = '';
                        this.namedQueryObject.model.query = '';
                    }
                }

                if (this.validateName) {
                    this.dataSource.data.map(function (row, index) {
                        if (isName) {
                            row.query = index < originDataSource.data.length ? originDataSource.data[index].query : '';
                        } else {
                            row.name = index < originDataSource.data.length ? originDataSource.data[index].name : '';
                        }
                        return row;
                    });

                    this.dataSource.data.slice().reverse().forEach((row, index, object) => {
                        if (row && row.name === '' && row.query === '') {
                            this.dataSource.splice(object.length - 1 - index, 1);
                        }
                    });
                }

                if (!isMaxRowsLimitReached) {
                    this.addRow(new DrNamedQuery({ name: '', query: '', description: null, annotationText: null }));
                }
                setTimeout(() => {
                    this.focusNextSiblingField(currentIndex, true, true);
                });
                this.dirty();
            }
        }
    }

    trackByFn(index: number) {
        return index;
    }

    validateCharacters(name: string, rowIndex: number): void {
        const trimmedName = DrObject.trimLeadingWhiteSpaces(name);
        const validateNameError = this.namedQueryObject.uiValidateName(trimmedName);
        this.displayError(rowIndex, validateNameError);
        this.dirty();
    }

    getDuplicateNameErrorMessage(name: string, isFileExtension?: boolean): string {
        const trimmedName = name.trim();
        const message = '"' + trimmedName + '"' + ' already exists' + (!isFileExtension ? '.' : ' in the list.') + (!isFileExtension ? ' It must be unique within the Template.' : ' File extensions must be unique.');
        return message;
    }

    keyup(isName: boolean, rowIndex: number, row, last, event): void {
        // key board clicks - up/down/right/left should not mean an edited row
        if (event.keyCode !== 37 && event.keyCode !== 38 && event.keyCode !== 39 && event.keyCode !== 40) {
            if (row && row.hasError) {
                row.hasError = false;
                row.errorText = '';
            }
            if (row && row.isWarning) {
                row.isWarning = false;
                row.errorText = '';
            }
            if (row && row.isWarningInQueryColumn) {
                row.isWarningInQueryColumn = false;
                row.errorText = '';
            }
            this.onAddNewRow(rowIndex, row, last);
            if (this.queriesChanged) {
                // need to recalculate error indices as indices may change after rows are modified
                this.recalculateErrorIndices();
                this.queriesChanged(undefined, event?.type);
            }
        }
        if (this.isEmail) {
            this.popoverErrors[rowIndex].error = false;
            this.popoverErrors[rowIndex].popover = false;
            this.dirty();
        }
    }

    clearTextOnBackSpace(row: any, isName: boolean): void {
        if (row) {
            const text = isName ? row.name : row.query;
            if (text && ((text.length === 1) || (window.getSelection().toString().length === text.length))) {
                if (isName) {
                    this.isClearedByBackspace = true;
                }
            }
        }
    }

    resetState(): void {
        this.isDirtyMapInitialized = false;
        this.resetKeyValueMaps();
        this.isOriginalKeyValueDeleted = false;
        this.keyValueForm.form.markAsPristine();
        this.popoverErrors.length = this.rows.length;
        for (let index = 0; index < this.popoverErrors.length; index++) {
            this.popoverErrors[index] = {
                error: false,
                popover: false,
                message: null
            };
        }
        this.isTextPaste = false;
    }

    dirty(): boolean {
        if (!isUndefined(this.trackDirty) && (!this.trackDirty)) {
            return;
        }

        this.keyValueForm.form.markAsPristine();
        this.valid = true;

        // we have errors so 'Save' button can't be enabled.
        setTimeout(() => { this.valid = !this.hasErrors(); }, 100);

        let isDirty = false;
        let originalNamedQueryObject = null;
        let currentNamedQueryObject = null;

        const indexes = Array.from(this.originalKeyValueMap.keys());
        // do not count indices of empty key value pairs ECASL-35793
        this.currentKeyValueMap.forEach((value: DrNamedQuery, key: any) => {
            if (value.name === '' && value.query === '') {
                this.currentKeyValueMap.delete(key);
            }
        });
        const currentIndexes = Array.from(this.currentKeyValueMap.keys());

        // original list has same number of elements, have we new added elements
        if (this.isOriginalKeyValueDeleted || (!this.isOriginalKeyValueDeleted && (currentIndexes.length !== indexes.length))) {
            isDirty = true;
        }

        // has any of the original elements changed?
        for (let index = 0; (index < indexes.length) && !isDirty && !this.isOriginalKeyValueDeleted; index++) {
            // get original object at the specified index, if it exists;
            if (this.originalKeyValueMap.has(indexes[index])) {
                originalNamedQueryObject = this.originalKeyValueMap.get(indexes[index]);
            }

            if (this.currentKeyValueMap.has(indexes[index])) {
                currentNamedQueryObject = this.currentKeyValueMap.get(indexes[index]);
            } else {
                // original element has been deleted
                this.isOriginalKeyValueDeleted = true;
                isDirty = true;
            }

            // an original element has changed
            if (!isDirty && originalNamedQueryObject != null) {
                if (originalNamedQueryObject.name !== currentNamedQueryObject.name) {
                    isDirty = true;
                }

                if (originalNamedQueryObject.query !== currentNamedQueryObject.query) {
                    isDirty = true;
                }
            }
        }

        if (isDirty) {
            this.keyValueForm.form.markAsDirty();
        }

        return isDirty;
    }

    initializeDirtyMap(last?: boolean): void {
        if ((!isUndefined(this.trackDirty) && (!this.trackDirty)) || this.isDirtyMapInitialized) {
            return;
        }

        if (!isUndefined(this.rows) && (isUndefined(this.originalKeyValueMap) && (this.originalKeyValueMap !== null))) {
            this.currentKeyValueMap = new Map<number, DrNamedQuery>();
            this.originalKeyValueMap = new Map<number, DrNamedQuery>();
        }

        if (!isUndefined(this.rows)) {
            let lastRow = null;
            const finalRows = Object.assign([], this.rows);
            if (!isUndefined(last) && last !== null) {
                lastRow = finalRows.splice(-1, 1);
            }

            if (!isUndefined(this.originalKeyValueMap) && (this.originalKeyValueMap !== null)) {
                let index = 0;
                _.each(finalRows, (aNamedQueryRow) => {
                    if (_.isEmpty(aNamedQueryRow.name?.trim()) === false) {
                        this.addKeyValueObject(index, finalRows[index].name, true, finalRows[index].query);
                        index++;
                    }
                });

                if (lastRow != null) {
                    this.addKeyValueObject(this.currentKeyValueMap.size, lastRow.name, false, lastRow.query);
                }

                this.isDirtyMapInitialized = true;
            }
        }
    }

    addKeyValueObject(index: number, name: string, addToOriginal: boolean, query?: string): void {
        if (name) {
            this.namedQueryObject.model.name = name.trim();
        }

        if (query) {
            this.namedQueryObject.model.query = query.trim();
        }

        const object = new DrNamedQuery(this.namedQueryObject.model);
        this.addKeyValueToMap(index, new DrNamedQuery(object), addToOriginal);

        this.namedQueryObject.model.name = '';
        this.namedQueryObject.model.query = '';
    }

    addKeyValueToMap(index: number, nameQueryObject: DrNamedQuery, addToOriginal: boolean): void {
        if (!isUndefined(nameQueryObject)) {
            // will initialize map, if not already. This can be the case if the list is empty on start up.
            this.currentKeyValueMap.set(index, nameQueryObject);

            if (addToOriginal) {
                this.originalKeyValueMap.set(index, nameQueryObject);
            }
        }
    }

    resetKeyValueMaps(): void {
        this.currentKeyValueMap = new Map<number, DrNamedQuery>();
        this.originalKeyValueMap = new Map<number, DrNamedQuery>();
        this.initializeDirtyMap();
    }

    hasErrors(): boolean {
        const errorsExist = this.popoverErrors.filter(pe => pe.error).length + this.rows.filter(r => r.hasError).length;
        return errorsExist > 0;
    }

    scrollToFirstError(isBulkSearch = false): void {
        if (this.cdkVirtualScrollViewport) {
            if (isBulkSearch) {
                this.currentFocusedIndex = this.errorIndices[0];
                this.scrollToNextError(true);
            } else {
                for (let index = 0; index < this.popoverErrors.length; index++) {
                    if (this.popoverErrors[index] && this.popoverErrors[index].error === true) {
                        this.cdkVirtualScrollViewport.scrollToIndex(index);
                        break;
                    }
                }
            }
        }
    }

    scrollToNextError(scrollToFirstErroronValidate?: boolean) {
        const currentIndex = this.errorIndices.indexOf(this.currentFocusedIndex);
        if (currentIndex !== -1) {
            this.currentFocusedIndex = currentIndex === this.errorIndices.length - 1 || scrollToFirstErroronValidate ? this.errorIndices[0] : this.errorIndices[currentIndex + 1];
            this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
        } else {
            for (let i = 0; i < this.errorIndices.length; i++) {
                if (((i !== this.errorIndices.length - 1) && (this.errorIndices[i] < this.currentFocusedIndex) && (this.errorIndices[i + 1] > this.currentFocusedIndex))) {
                    this.currentFocusedIndex = this.errorIndices[i + 1];
                    this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
                    break;
                } else if (i === this.errorIndices.length - 1) {
                    this.currentFocusedIndex = this.errorIndices[0];
                    this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
                }
            }
        }
    }

    scrollToPreviousError() {
        const currentIndex = this.errorIndices.indexOf(this.currentFocusedIndex);
        if (currentIndex !== -1) {
            this.currentFocusedIndex = currentIndex === 0 ? this.errorIndices[this.errorIndices.length - 1] : this.errorIndices[currentIndex - 1];
            this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
        } else {
            for (let i = this.errorIndices.length - 1; i >= 0; i--) {
                if (((this.errorIndices[i] > this.currentFocusedIndex) && (this.errorIndices[i - 1] < this.currentFocusedIndex))) {
                    this.currentFocusedIndex = this.errorIndices[i - 1];
                    this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
                    break;
                } else if (i === 0) {
                    this.currentFocusedIndex = this.errorIndices[this.errorIndices.length - 1];
                    this.cdkVirtualScrollViewport.scrollToIndex(this.currentFocusedIndex);
                }
            }
        }
    }

    recalculateErrorIndices(): void {
        this.errorIndices = [];
        this.rows.forEach((rowInRows, index) => {
            if (rowInRows.hasError) {
                this.errorIndices.push(index);
            }
        });
    }

    async validateQueries(KeyValueListComponent: KeyValueListComponent, queries: DrNamedQuery[], isOnlyQueryColumn: boolean = false): Promise<boolean> {
        // 1. First layer of validation in the UI - Metadata validation
        let hasError = false;
        KeyValueListComponent.errorIndices = [];

        const validMetadataFields = await this.metadataService.tokenizationTypes();
        queries = DrNamedQuery.removeStrictEmptyQueries(queries);

        for (let i = 0; i < queries.length; i++) {
            const invalidMetadataResults = this.metadataService.findInvalidMetadataFields(queries[i].query, validMetadataFields, this.settingsType);

            if (invalidMetadataResults.length > 0) {
                const firstInvalid = invalidMetadataResults[0];
                queries[i].hasError = true;
                queries[i].errorText = `Invalid metadata field \"${firstInvalid.fullMatch}\"`;
                KeyValueListComponent.errorIndices.push(i);
                hasError = true;
            } else {
                queries[i].hasError = false;
            }
        }

        if (hasError) {
            return Promise.resolve(false);
        }

        // 2. Second layer of validation using backend API - Queries validation
        hasError = false;
        KeyValueListComponent.errorIndices = [];

        return new Promise<boolean>((resolve, reject) => {
            const requestData = this.createQueriesValidationRequest(queries, isOnlyQueryColumn);

            this.restService.post(Endpoint.viewManager_validateQueries, requestData).then(response => {
                if (response && response.searchResponse) {
                    const searchResponse = response.searchResponse;

                    for (let i = 0; i < searchResponse.length; i++) {
                        if (searchResponse[i].extendedStatus) {
                            queries[i].hasError = true;
                            queries[i].isWarningInQueryColumn = searchResponse[i].warning ?? false;
                            queries[i].errorText = searchResponse[i].extendedStatus;
                            KeyValueListComponent.errorIndices.push(i);
                            hasError = true;
                        } else {
                            queries[i].hasError = false;
                        }
                    }

                    resolve(!hasError);
                }
            }, error => {
                reject(error);
            });
        });
    }

    private createQueriesValidationRequest(queries: DrNamedQuery[], isOnlyQueryColumn: boolean): any {
        const requestData = { queries: [] };

        const nonEmptyQueries = isOnlyQueryColumn ? DrNamedQuery.removeStrictEmptyQueries(queries) : DrNamedQuery.removeEmptyQueries(queries);
        const xmlQueries = [];

        nonEmptyQueries.map(q => q.query).forEach((query) => {
            let queryString = query.trim().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ' ');
            xmlQueries.push(this.xmlService.stringToXMLQuery(queryString));
        });

        requestData.queries = xmlQueries;
        return requestData;
    }

    iconClick(errorText: string, isWarning: boolean = false): void {
        if (this.isNameValidationContext || this.isQueryValidationContext) {
            isWarning ? this.commonService.openWarningModal(errorText) : this.commonService.openErrorModal(errorText);
        }
    }

    getNameColIconTitle(row: any): string {
        if (!this.isNameValidationContext) return '';
        return row.isWarning ? 'Warning. Click for details.' : 'Error. Click for details.';
    }

    getNameColIconClass(row: any): string {
        const baseClass = 'fa';
        const colorClass = row.isWarning ? 'color-tan-07 warning-icon' : 'color-red-05 error-icon';
        return `${baseClass} ${colorClass}`;
    }

    getQueryColIconTitle(row: any): string {
        return row.isWarningInQueryColumn ? 'Warning. Click for details.' : 'Error. Click for details.';
    }

    getQueryColIconClass(row: any): string {
        let baseClass = 'fa color-red-05 error-icon second-field-icon';
        if (row.isWarningInQueryColumn) {
            baseClass = baseClass
                .replace('color-red-05', 'color-tan-07')
                .replace('error-icon', 'warning-icon');
        }
        return baseClass;
    }
}